import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthToken } from '@/lib/auth';
import { HelpCircle, CheckCircle, Target, Flame, Play, ClipboardList, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface UserStats {
  totalQuestions: number;
  attemptedQuestions: number;
  correctAnswers: number;
  currentStreak: number;
  averageScore: number;
}

interface PracticeHistory {
  id: number;
  category: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeTaken: number;
  createdAt: string;
}

interface DashboardProps {
  onSectionChange: (section: string) => void;
}

const apiURL = import.meta.env.VITE_API_URL || "http://localhost:7845";

export function Dashboard({ onSectionChange }: DashboardProps) {
  const { data: stats, isLoading: statsLoading } = useQuery<UserStats>({
    queryKey: ['/api/stats'],
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch(apiURL + `/api/examination/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json();
    },
  });

  const { data: recentHistory, isLoading: historyLoading } = useQuery<PracticeHistory[]>({
    queryKey: ['/api/history'],
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch('/api/history', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch history');
      return response.json();
    },
  });

  const handleQuickPractice = () => {
    onSectionChange('mcq');
  };

  const handleMockTest = () => {
    onSectionChange('exam');
  };

  const handleViewProgress = () => {
    onSectionChange('history');
  };

  if (statsLoading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card className="gcep-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Questions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.totalQuestions || 0}
                </p>
              </div>
              <div className="bg-[hsl(201,91%,60%)] bg-opacity-10 p-3 rounded-lg">
                <HelpCircle className="h-6 w-6 text-[hsl(201,91%,60%)]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="gcep-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Questions Attempted</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.attemptedQuestions || 0}
                </p>
              </div>
              <div className="bg-[hsl(142,76%,36%)] bg-opacity-10 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-[hsl(142,76%,36%)]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="gcep-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Accuracy Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.averageScore || 0}%
                </p>
              </div>
              <div className="bg-[hsl(45,93%,47%)] bg-opacity-10 p-3 rounded-lg">
                <Target className="h-6 w-6 text-[hsl(45,93%,47%)]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="gcep-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Study Streak</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.currentStreak || 0} days
                </p>
              </div>
              <div className="bg-orange-500 bg-opacity-10 p-3 rounded-lg">
                <Flame className="h-6 w-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <Card className="gcep-card hover:shadow-xl transition duration-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="bg-[hsl(201,91%,60%)] bg-opacity-10 p-3 rounded-lg">
                <Play className="h-6 w-6 text-[hsl(201,91%,60%)]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Practice</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Start practicing now</p>
              </div>
            </div>
            <Button onClick={handleQuickPractice} className="w-full gcep-primary-btn">
              Start Practice
            </Button>
          </CardContent>
        </Card>

        <Card className="gcep-card hover:shadow-xl transition duration-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="bg-[hsl(142,76%,36%)] bg-opacity-10 p-3 rounded-lg">
                <ClipboardList className="h-6 w-6 text-[hsl(142,76%,36%)]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Mock Test</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Take a timed exam</p>
              </div>
            </div>
            <Button onClick={handleMockTest} className="w-full gcep-success-btn">
              Take Test
            </Button>
          </CardContent>
        </Card>

        <Card className="gcep-card hover:shadow-xl transition duration-300">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="bg-purple-500 bg-opacity-10 p-3 rounded-lg">
                <BarChart3 className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Progress Report</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">View your performance</p>
              </div>
            </div>
            <Button onClick={handleViewProgress} className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold">
              View Report
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {/* <Card className="gcep-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between py-3">
                  <div className="flex items-center space-x-3">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <div>
                      <Skeleton className="h-4 w-32 mb-1" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Skeleton className="h-6 w-12 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentHistory && recentHistory.length > 0 ? (
            <div className="space-y-4">
              {recentHistory.slice(0, 5).map((activity) => (
                <div key={activity.id} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <div className="bg-[hsl(201,91%,60%)] bg-opacity-10 p-2 rounded-lg">
                      <HelpCircle className="h-5 w-5 text-[hsl(201,91%,60%)]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {activity.category.charAt(0).toUpperCase() + activity.category.slice(1)} Practice
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(activity.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">{activity.score}%</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {activity.correctAnswers}/{activity.totalQuestions} questions
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400 text-center py-8">
              No practice history yet. Start practicing to see your progress here!
            </p>
          )}
        </CardContent>
      </Card> */}
    </div>
  );
}
